import { Plus, Trash2, Check } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";

// The splits we already use with clients, with labels that say when each piece
// is due. Labels are just a starting point — every one is editable per row.
export const PLAN_PRESETS = [
  { key: "full", name: "Paid in full", rows: [[100, "Paid in full — on invoice"]] },
  { key: "50-50", name: "50 / 50", rows: [
    [50, "Deposit — on signing"],
    [50, "Balance — on final delivery"],
  ] },
  { key: "50-25-25", name: "50 / 25 / 25", rows: [
    [50, "Deposit — on signing"],
    [25, "Second payment — on V1 delivery"],
    [25, "Final payment — on final delivery"],
  ] },
  { key: "25-50-25", name: "25 / 50 / 25", rows: [
    [25, "Deposit — on signing"],
    [50, "Second payment — at midpoint"],
    [25, "Final payment — on final delivery"],
  ] },
  { key: "75-25", name: "75 / 25", rows: [
    [75, "Upfront — on signing"],
    [25, "Balance — on final delivery"],
  ] },
];

const newId = () =>
  (window.crypto?.randomUUID ? window.crypto.randomUUID() : `inst-${Date.now()}-${Math.random().toString(36).slice(2)}`);

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const today = () => new Date().toISOString().split("T")[0];

// Unpaid amounts follow their percentages. A paid installment is money already
// sent, so its amount never moves — only its share of the new total does. When
// the split added up to 100%, the last unpaid row takes the remainder so the
// plan still lands on the exact total.
export function rescaleInstallments(installments, total) {
  if (!installments?.length) return installments || [];
  const pctSum = installments.reduce((s, i) => s + (Number(i.percent) || 0), 0);
  const balanced = Math.abs(pctSum - 100) < 0.01 && installments.every((i) => i.percent != null);
  const rows = installments.map((i) => {
    if (i.paid) return { ...i, percent: total > 0 ? round2((i.amount / total) * 100) : i.percent };
    return { ...i, amount: i.percent != null ? round2((total * i.percent) / 100) : round2(i.amount) };
  });
  const lastUnpaid = rows.map((r) => !r.paid).lastIndexOf(true);
  if (balanced && lastUnpaid >= 0) {
    const others = rows.reduce((s, r, idx) => (idx === lastUnpaid ? s : s + r.amount), 0);
    rows[lastUnpaid].amount = round2(total - others);
    if (total > 0) rows[lastUnpaid].percent = round2((rows[lastUnpaid].amount / total) * 100);
  }
  return rows;
}

export function buildPresetInstallments(preset, total, firstDueDate) {
  const rows = preset.rows.map(([percent, label], idx) => ({
    id: newId(),
    label,
    percent,
    amount: 0,
    due_date: idx === 0 ? firstDueDate || "" : "",
    paid: false,
    paid_date: null,
  }));
  return rescaleInstallments(rows, total);
}

// Same rule the backend applies (services/vendor_payment_plan.py) so the
// popup shows the status the save will produce.
export function derivePlanStatus(installments, current) {
  if (current === "disputed") return "disputed";
  if (!installments?.length) return current;
  const paid = installments.filter((i) => i.paid).length;
  if (paid === installments.length) return "paid";
  return paid > 0 ? "partially_paid" : "unpaid";
}

export function nextUnpaidInstallment(installments) {
  const unpaid = (installments || []).filter((i) => !i.paid);
  const dated = unpaid.filter((i) => i.due_date).sort((a, b) => a.due_date.localeCompare(b.due_date));
  return dated[0] || unpaid[0] || null;
}

export function formatShortDate(d) {
  if (!d) return "";
  const date = new Date(`${d}T00:00:00`);
  if (Number.isNaN(date.getTime())) return d;
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }) });
}

// Number inputs without the browser's spin arrows, which eat half of a narrow box.
const NO_SPIN = "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none";

const usd = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n || 0);

export default function VendorPaymentPlanEditor({ installments, total, firstDueDate, onChange }) {
  const rows = installments || [];
  const scheduled = round2(rows.reduce((s, i) => s + (Number(i.amount) || 0), 0));
  const paidTotal = round2(rows.filter((i) => i.paid).reduce((s, i) => s + (Number(i.amount) || 0), 0));
  const gap = round2(total - scheduled);

  const applyPreset = (preset) => {
    const paidCount = rows.filter((i) => i.paid).length;
    if (paidCount && !window.confirm(`Replace this schedule? ${paidCount} payment${paidCount > 1 ? "s are" : " is"} already marked paid.`)) return;
    onChange(buildPresetInstallments(preset, total, firstDueDate));
  };

  const update = (idx, patch) => onChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const setPercent = (idx, value) => {
    const percent = value === "" ? null : Number(value);
    update(idx, { percent, amount: percent == null ? rows[idx].amount : round2((total * percent) / 100) });
  };

  // Typing a dollar figure pins that row to dollars and re-derives its percent.
  const setAmount = (idx, value) => {
    const amount = value === "" ? 0 : Number(value);
    update(idx, { amount, percent: total > 0 ? round2((amount / total) * 100) : null });
  };

  const togglePaid = (idx, paid) =>
    update(idx, { paid, paid_date: paid ? rows[idx].paid_date || today() : null });

  const addRow = () =>
    onChange([
      ...rows,
      {
        id: newId(),
        label: rows.length ? "" : "Deposit — on signing",
        percent: total > 0 && gap > 0 ? round2((gap / total) * 100) : null,
        amount: gap > 0 ? gap : 0,
        due_date: rows.length ? "" : firstDueDate || "",
        paid: false,
        paid_date: null,
      },
    ]);

  const activePreset = PLAN_PRESETS.find(
    (p) => p.rows.length === rows.length && p.rows.every(([pct], i) => Number(rows[i]?.percent) === pct)
  );

  return (
    <div className="space-y-3" data-testid="vendor-payment-plan-editor">
      <div className="flex flex-wrap gap-1.5">
        {PLAN_PRESETS.map((p) => (
          <Button
            key={p.key}
            type="button"
            size="sm"
            variant={activePreset?.key === p.key ? "default" : "outline"}
            className="h-7 px-2.5 text-xs"
            onClick={() => applyPreset(p)}
            data-testid={`plan-preset-${p.key}`}
          >
            {p.name}
          </Button>
        ))}
      </div>

      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          Pick a split above, or add payments one by one.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((row, idx) => (
            <div
              key={row.id || idx}
              className={`rounded-md border p-2 space-y-2 ${row.paid ? "bg-muted/40" : ""}`}
              data-testid={`plan-installment-${idx}`}
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground w-4 shrink-0">{idx + 1}</span>
                <Input
                  value={row.label || ""}
                  onChange={(e) => update(idx, { label: e.target.value })}
                  placeholder="What is this payment for? e.g. Deposit — on signing"
                  className="h-8 text-sm"
                  data-testid={`plan-installment-label-${idx}`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 shrink-0"
                  onClick={() => onChange(rows.filter((_, i) => i !== idx))}
                  title="Remove this payment"
                >
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                </Button>
              </div>
              <div className="grid grid-cols-[72px_1fr_1fr_auto] gap-2 items-center pl-6">
                <div className="relative">
                  <Input
                    type="number"
                    value={row.percent ?? ""}
                    onChange={(e) => setPercent(idx, e.target.value)}
                    className={`h-8 text-sm pr-6 ${NO_SPIN}`}
                    aria-label="Percent of total"
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                </div>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                  <Input
                    type="number"
                    value={row.amount ?? ""}
                    onChange={(e) => setAmount(idx, e.target.value)}
                    className={`h-8 text-sm pl-5 ${NO_SPIN}`}
                    aria-label="Amount"
                  />
                </div>
                <Input
                  type="date"
                  value={row.due_date || ""}
                  onChange={(e) => update(idx, { due_date: e.target.value })}
                  className="h-8 text-sm"
                  aria-label="Due date"
                  title="Due date"
                />
                <label className="flex items-center gap-1.5 text-xs cursor-pointer whitespace-nowrap">
                  <Checkbox
                    checked={!!row.paid}
                    onCheckedChange={(v) => togglePaid(idx, !!v)}
                    data-testid={`plan-installment-paid-${idx}`}
                  />
                  Paid
                </label>
              </div>
              {row.paid && (
                <div className="flex items-center gap-2 pl-6 text-xs text-muted-foreground">
                  <Check className="w-3 h-3 text-emerald-600" />
                  Paid on
                  <Input
                    type="date"
                    value={row.paid_date || ""}
                    onChange={(e) => update(idx, { paid_date: e.target.value || today() })}
                    className="h-7 w-36 text-xs"
                    aria-label="Paid date"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={addRow}>
          <Plus className="w-3 h-3 mr-1" />
          Add payment
        </Button>
        {rows.length > 0 && (
          <div className="text-xs text-right">
            <span className="text-muted-foreground">Scheduled {usd(scheduled)} of {usd(total)}</span>
            {Math.abs(gap) >= 0.01 ? (
              <span className="text-amber-600 font-medium"> · {gap > 0 ? `${usd(gap)} not scheduled` : `${usd(-gap)} over`}</span>
            ) : (
              <span className="text-emerald-600"> · adds up</span>
            )}
            <div className="text-muted-foreground">Paid so far {usd(paidTotal)}</div>
          </div>
        )}
      </div>
    </div>
  );
}
